import type { DynamicDataTableProps, DynamicDatatableProperty } from '@canvas-builder/types/canvas'

/**
 * Calculate the background color for a table row based on striping configuration
 * @param idx - The row index (0-based)
 * @param data - The dynamic data table props containing striping configuration
 * @param themeVars - The theme variables containing color values
 * @returns The background color string for the row
 */
export function getRowBackgroundColor(
  idx: number,
  data: DynamicDataTableProps | undefined,
  themeVars: Record<string, any>,
): string {
  // Default to true unless explicitly set to false for backwards compatibility
  const enableRowStriping = data?.enable_row_striping !== false

  if (!enableRowStriping) {
    return themeVars.base
  }

  // Default to false unless explicitly set to true
  const stripingStartFromFirst = data?.striping_start_from_first ?? false
  const shouldStripe = stripingStartFromFirst
    ? !!((idx + 1) % 2) // Start from first row (0, 2, 4...)
    : !!(idx % 2) // Start from second row (1, 3, 5...)

  if (shouldStripe) {
    // Default to primary6_1 if not explicitly set
    return data?.striping_color ?? themeVars.primary6_1
  }

  return themeVars.base
}

/**
 * Format a cell value based on the property's format configuration
 * @param value - The raw value to format
 * @param property - The property configuration including format settings
 * @returns The formatted string representation of the value
 */
export function formatCellValue(value: any, property: DynamicDatatableProperty): string {
  if (value == null || value === '') {
    return ''
  }

  if (!property.format?.type) {
    return String(value)
  }

  const format = property.format

  switch (format.type) {
    case 'currency': {
      const num = parseFloat(value)
      if (isNaN(num)) return String(value)

      const currency = format.currency ?? 'USD'
      const decimals = format.decimals ?? 2
      const thousandsSeparator = format.thousandsSeparator ?? true

      const formatted = num.toFixed(decimals)
      const parts = formatted.split('.')

      if (thousandsSeparator) {
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }

      const symbols: Record<string, string> = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        JPY: '¥',
        CHF: 'CHF ',
        CAD: 'C$',
        AUD: 'A$',
      }

      const symbol = symbols[currency] ?? currency + ' '
      return symbol + (parts.length > 1 ? parts.join('.') : parts[0])
    }

    case 'number': {
      const num = parseFloat(value)
      if (isNaN(num)) return String(value)

      const decimals = format.decimals ?? 0
      const thousandsSeparator = format.thousandsSeparator ?? true

      const formatted = num.toFixed(decimals)
      const parts = formatted.split('.')

      if (thousandsSeparator) {
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }

      return parts.length > 1 ? parts.join('.') : parts[0]
    }

    case 'percentage': {
      const num = parseFloat(value)
      if (isNaN(num)) return String(value)

      const decimals = format.decimals ?? 2
      const thousandsSeparator = format.thousandsSeparator ?? false

      const percentValue = num * 100
      const formatted = percentValue.toFixed(decimals)
      const parts = formatted.split('.')

      if (thousandsSeparator) {
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }

      return (parts.length > 1 ? parts.join('.') : parts[0]) + '%'
    }

    case 'date': {
      const dateFormat = format.dateFormat ?? 'MM/DD/YYYY'

      try {
        const date = new Date(value)
        if (isNaN(date.getTime())) return String(value)

        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const year = date.getFullYear()
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        const monthName = monthNames[date.getMonth()]

        switch (dateFormat) {
          case 'MM/DD/YYYY':
            return `${month}/${day}/${year}`
          case 'DD/MM/YYYY':
            return `${day}/${month}/${year}`
          case 'YYYY-MM-DD':
            return `${year}-${month}-${day}`
          case 'MMM DD, YYYY':
            return `${monthName} ${day}, ${year}`
          case 'DD MMM YYYY':
            return `${day} ${monthName} ${year}`
          default:
            return `${month}/${day}/${year}`
        }
      } catch {
        return String(value)
      }
    }

    default:
      return String(value)
  }
}
